import Api from "../../services/Api.ts";
import {useEffect, useState} from "react";
import {useAtom, useSetAtom} from 'jotai';
import { categoriesAtom, selectedDepartmentsAtom, matchingClientsAtom } from '../../jotai_state';
import TooFewClients from "../../components/TooFewClients/TooFewClients.tsx";
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend } from 'chart.js';
import ValueSlider from "../../components/Sliders/ValueSlider.tsx";

ChartJS.register(CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend);

interface LoggedInInterface {
  total: number;
  last30: number;
  last7: number;
  weeks: Array<any>;
}

interface ActivationInterface {
  summary: Array<any>;
  aiAccumulated: Array<any>;
}

export default function Activation() {
  const api = Api.getInstance();
  const [tooFewClients, setTooFewClients] = useState<number|undefined>(undefined);
  const [loggedIn, setLoggedIn] = useState<LoggedInInterface|null>(null);
  const [activation, setActivation] = useState<ActivationInterface|null>(null);
  const [aiAccumulated, setAiAccumulated] = useState<Array<any>>([]);
  const [categories] = useAtom(categoriesAtom);
  const [clients, setClients] = useState(0);
  const [selectedDepartments] = useAtom(selectedDepartmentsAtom);
  const setMatchingClients = useSetAtom(matchingClientsAtom);
  const [aiUsageFrom, setAiUsageFrom] = useState(0);
  const [aiUsageNr, setAiUsageNr] = useState(10);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getData()
  }, [categories, selectedDepartments]);

  const getData = () => {
    const data = {
      categories: categories,
      departments: selectedDepartments,
    }

    api.post('customer/sprint_activation', data).then((response) => {
      if (response.json.data.tooFewClients !== undefined) {
        setTooFewClients(response.json.data.tooFewClients)
      } else {
        setLoggedIn(response.json.data.loggedIn);
        setActivation(response.json.data.activationPerWeek);
        setMatchingClients(response.json.data.matchingClients);
        setClients(response.json.data.matchingClients);
        setAiAccumulated(response.json.data.aiAccumulated)
        setAiUsageNr(response.json.data.aiAccumulated.length)
        setTooFewClients(undefined);
      }
      setLoaded(true);
    }).catch(error => {
      setError('Unknown error (http status ' + error.response.status + ')')
      setLoaded(true)
    })
  }

  const renderLoggedIn = () => {

    if (!loggedIn) return null;

    const labels = [];
    const data = [];
    for (const week of loggedIn.weeks) {
      labels.push(week.week);
      data.push(week.percent)
    }

    return (
      <div className="mt-4 z-box large white">
        <div className="flex gap-x-4">
          <div className="z-box w-1/3">
            <div>Total</div>
            <div className="z-h2">{loggedIn?.total.toFixed(0)}%</div>
          </div>
          <div className="z-box w-1/3">
            <div>LAST 30 DAYS</div>
            <div className="z-h2">{loggedIn?.last30.toFixed(0)}%</div>
          </div>
          <div className="z-box w-1/3">
            <div>LAST 7 DAYS</div>
            <div className="z-h2">{loggedIn?.last7.toFixed(0)}%</div>
          </div>
        </div>

        <div className="mt-4 z-body1">Share of users logged in per week</div>
        <Line
          data={{
            labels: labels,
            datasets: [{
              data: data,
              backgroundColor: '#6E89F5',
              borderColor: '#6E89F5',
            }],
          }}
          options={{
            responsive: true,
            scales: { y: { min: 0, max: 100, ticks: { callback: (v) => v + '%' } } },
            plugins: { legend: { display: false } },
          }}
        />
      </div>
    )
  }

  const renderActivation = () => {
    if (!activation || !activation.summary) return null;
    const labels: Array<string> = [];
    const data: Array<number> = [];
    for (const item of activation.summary) {
      labels.push(item[0]);
      data.push(item[1]);
    }

    return (
      <div className="mt-4 z-box">
        <div className="mt-4 z-h2">Activation rate</div>
        <Bar
          data={{
            labels: labels,
            datasets: [{
              data: data,
              backgroundColor: '#6E89F5',
            }],
          }}
          options={{
            responsive: true,
            indexAxis: 'y',
            scales: { x: { min: 0, max: 100, ticks: { callback: (v) => v + '%' } } },
            plugins: { legend: { display: false } },
          }}
        />
      </div>
    )
  }

  const renderAiAccumulated = () => {

    const labels: Array<string> = [];
    const data: Array<number> = [];
    const accumulated: Array<number> = [];
    const itemMax = aiUsageFrom + aiUsageNr;
    for (const item of aiAccumulated.slice(aiUsageFrom, itemMax)) {
      labels.push(item.week);
      data.push(100 * item.count / clients);
      accumulated.push(item.total);
    }

    return (
      <div className="mt-4 z-box">

        <div className="mt-4 z-body1">Usage of AI platform</div>
        <div className="flex items-center my-4">
          <div className="h-3 w-3 rounded-full bg-primary-600"/>
          <div className="ml-2 z-body2">Clients per week</div>
          <div className="ml-4 h-3 w-3 rounded-full bg-aquamarine-600 "/>
          <div className="ml-2 z-body2">Accumulated AI-messages</div>
        </div>
        <Line
          data={{
            labels: labels,
            datasets: [
              {
                data: data,
                backgroundColor: '#6E89F5',
                borderColor: '#6E89F5',
                yAxisID: 'y',
              },
              {
                data: accumulated,
                backgroundColor: '#4CA28E',
                borderColor: '#4CA28E',
                yAxisID: 'y1',
              }
            ],
          }}
          options={{
            responsive: true,
            animation: {duration: 0},
            scales: {
              y:
                {
                  position: 'left',
                  min: 0, max: 100, ticks: {
                  callback: (v) => v + '%'
                }
              },
              y1:
                {
                  position: 'right',
                  min: 0,
                  grid: {
                    drawOnChartArea: false, // Förhindrar att stödritade linjer krockar med vänstra axeln
                  },
                }
            },
            plugins: { legend: { display: false } },
          }}
        />

        {
          aiAccumulated.length > 12 &&
            <>
                <div className="w-full mt-4">
                    <div className="z-body3">Number of weeks to show</div>
                    <div className="flex justify-between">
                        <div className="z-title3">12 weeks</div>
                        <div className="z-title3">Use slider to change how many weeks to view</div>
                        <div className="z-title3">{aiAccumulated.length} weeks</div>
                    </div>
                    <ValueSlider value={aiUsageNr} min={12} max={aiAccumulated.length} classes="mt-1"
                                 update={(val: number) => setAiUsageNr(val)}/>
                </div>

                <div className="w-full mt-4">
                    <div className="flex justify-between">
                        <div className="z-title3">{aiAccumulated[0].week}</div>
                        <div className="z-title3">Use slider to change first week</div>
                        <div className="z-title3">{aiAccumulated[aiAccumulated.length - 1].week}</div>
                    </div>
                    <ValueSlider value={aiUsageFrom} min={0} max={aiAccumulated.length - 12} classes="mt-1"
                                 update={(val: number) => setAiUsageFrom(val)}/>
                </div>


            </>
        }
      </div>
    )
  }

  if (!loaded) {
    return <div>Loading data...</div>
  }
  if (error.length > 0) {
    return <div>{error}</div>
  }
  if (tooFewClients !== undefined) {
    return <TooFewClients tooFewClients={tooFewClients}/>
  }

  return (
    <>
      <div className="z-h2">Activation data</div>
      <div className="z-body1 mt-4 text-gray-1000!">
        What is actually done in the platform — goals that are set, activities that are followed up
        and how usage develops week by week.
      </div>
      {renderLoggedIn()}
      {renderActivation()}
      {renderAiAccumulated()}
    </>
  );
}